import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  CheckCircle as SuccessIcon,
  LocalShipping as ContainerIcon,
  Assessment as ReportIcon,
  Error as ErrorIcon,
  HourglassEmpty as PendingIcon,
  Close as CloseIcon,
  InsertDriveFile as FileIcon
} from '@mui/icons-material';
import { api } from '../config/api';

interface ImportResult {
  success: boolean;
  processed_count?: number;  // Old endpoint
  matched_count?: number;     // Old endpoint
  containers_created?: number;
  container_ids?: number[];
  container_numbers?: string[];
  container_lines_created?: number;
  import_id?: number;
  filename?: string;
  error?: string;
  // New fields from process-packing-list-v2
  supplier?: string;
  po_number?: string;
  total_pallets_processed?: number;
  pallets_matched?: number;
  pallets_unmatched?: number;
  po_breakdown?: Array<{
    po_number: string | null;
    line_count: number;
    stock_codes: string[];
    unique_stocks: number;
  }>;
  warning?: string;  // Warning when items are skipped
  skipped_items?: number;  // Number of items skipped due to missing PO
}

interface FileUploadStatus {
  file: File;
  status: 'pending' | 'uploading' | 'success' | 'error';
  progress: number;
  result?: ImportResult;
  error?: string;
}

interface Container {
  id: number;
  container_no: string;
  po_number: string;
  status: string;
}

const PackingListImport: React.FC = () => {
  const { user } = useAuth();
  const [fileStatuses, setFileStatuses] = useState<FileUploadStatus[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [showContainers, setShowContainers] = useState(false);
  const [containers, setContainers] = useState<Container[]>([]);
  const [selectedResultIndex, setSelectedResultIndex] = useState<number | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const ACCEPTED_EXTENSIONS = ['.xlsx', '.xls', '.xlsm', '.xml', '.pdf'];

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    if (isProcessing) return;

    const droppedFiles = Array.from(e.dataTransfer.files).filter(file => {
      const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
      return ACCEPTED_EXTENSIONS.includes(ext);
    });

    if (droppedFiles.length > 0) {
      const newStatuses: FileUploadStatus[] = droppedFiles.map(file => ({
        file,
        status: 'pending',
        progress: 0
      }));
      setFileStatuses(newStatuses);
      setActiveStep(0);
      setSelectedResultIndex(null);
    }
  };

  const steps = [
    'Select Files',
    'Processing',
    'Review Results'
  ];

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (selectedFiles.length > 0) {
      const newStatuses: FileUploadStatus[] = selectedFiles.map(file => ({
        file,
        status: 'pending',
        progress: 0
      }));
      setFileStatuses(newStatuses);
      setActiveStep(0);
      setSelectedResultIndex(null);
    }
  };

  const removeFile = (index: number) => {
    setFileStatuses(prev => prev.filter((_, i) => i !== index));
  };

  const processAllFiles = async () => {
    if (fileStatuses.length === 0) return;

    setIsProcessing(true);
    setActiveStep(1);

    // Process files sequentially
    for (let i = 0; i < fileStatuses.length; i++) {
      const fileStatus = fileStatuses[i];

      // Update status to uploading
      setFileStatuses(prev => prev.map((fs, idx) =>
        idx === i ? { ...fs, status: 'uploading', progress: 10 } : fs
      ));

      try {
        // Create FormData for multipart upload
        const formData = new FormData();
        formData.append('file', fileStatus.file);
        if (user?.staff_no) {
          formData.append('staff_no', user.staff_no);
        }

        // Update progress
        setFileStatuses(prev => prev.map((fs, idx) =>
          idx === i ? { ...fs, progress: 50 } : fs
        ));

        // Send to API using the v2 endpoint that supports both Excel and XML
        const response = await api.post(
          '/containers/process-packing-list-v2',
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
            timeout: 120000
          }
        );

        // Update status to success
        setFileStatuses(prev => prev.map((fs, idx) =>
          idx === i ? { ...fs, status: 'success', progress: 100, result: response.data } : fs
        ));

      } catch (err: any) {
        console.error(`Error processing ${fileStatus.file.name}:`, err);

        const errorData = err.response?.data;
        let errorMessage = 'Failed to process packing list';

        if (errorData) {
          if (errorData.error_type === 'missing_po') {
            errorMessage = `PO Number Missing: ${errorData.error}`;
          } else if (errorData.error) {
            errorMessage = errorData.error;
          }
        } else if (err.message) {
          errorMessage = err.message;
        }

        // Update status to error
        setFileStatuses(prev => prev.map((fs, idx) =>
          idx === i ? { ...fs, status: 'error', progress: 100, error: errorMessage } : fs
        ));
      }
    }

    setIsProcessing(false);
    setActiveStep(2);

    // Fetch container details for all successful uploads
    const allContainerIds = fileStatuses
      .filter(fs => fs.result?.container_ids)
      .flatMap(fs => fs.result!.container_ids!);

    if (allContainerIds.length > 0) {
      await fetchContainerDetails(allContainerIds);
    }
  };

  const fetchContainerDetails = async (containerIds: number[]) => {
    try {
      const uniqueIds = Array.from(new Set(containerIds));
      const containerPromises = uniqueIds.map(id =>
        api.get(`/containers/${id}`)
      );

      const responses = await Promise.all(containerPromises);
      const containerData = responses.map(response => response.data);
      setContainers(containerData);
    } catch (err) {
      console.error('Error fetching container details:', err);
    }
  };

  const handleViewContainers = () => {
    setShowContainers(true);
  };

  const getStatusIcon = (status: FileUploadStatus['status']) => {
    switch (status) {
      case 'pending': return <PendingIcon color="disabled" />;
      case 'uploading': return <CircularProgress size={24} />;
      case 'success': return <SuccessIcon color="success" />;
      case 'error': return <ErrorIcon color="error" />;
    }
  };

  const getStatusColor = (status: FileUploadStatus['status']) => {
    switch (status) {
      case 'pending': return 'grey.500';
      case 'uploading': return 'primary.main';
      case 'success': return 'success.main';
      case 'error': return 'error.main';
    }
  };

  const successCount = fileStatuses.filter(fs => fs.status === 'success').length;
  const errorCount = fileStatuses.filter(fs => fs.status === 'error').length;
  const totalContainersCreated = fileStatuses
    .filter(fs => fs.result?.containers_created)
    .reduce((sum, fs) => sum + (fs.result?.containers_created || 0), 0);

  const selectedResult = selectedResultIndex !== null ? fileStatuses[selectedResultIndex]?.result : null;

  return (
    <Box sx={{ maxWidth: 1000, margin: '0 auto', p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Packing List Import
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Upload one or more packing list files (Excel or XML) to automatically create containers with stock code matching.
      </Typography>

      {/* Progress Stepper */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </CardContent>
      </Card>

      {/* File Upload Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={8}>
              <input
                accept=".xlsx,.xls,.xlsm,.xml,.pdf"
                style={{ display: 'none' }}
                id="packing-list-upload"
                type="file"
                multiple
                onChange={handleFileSelect}
                disabled={isProcessing}
              />
              <label htmlFor="packing-list-upload">
                <Box
                  onDragOver={handleDragOver}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  sx={{
                    border: '2px dashed',
                    borderColor: isDragOver ? 'primary.main' : 'grey.400',
                    borderRadius: 2,
                    p: 4,
                    textAlign: 'center',
                    cursor: isProcessing ? 'default' : 'pointer',
                    backgroundColor: isDragOver ? 'primary.50' : 'transparent',
                    transition: 'border-color 0.2s, background-color 0.2s',
                    opacity: isProcessing ? 0.5 : 1,
                    '&:hover': !isProcessing ? {
                      borderColor: 'primary.main',
                      backgroundColor: 'action.hover'
                    } : {}
                  }}
                >
                  <UploadIcon sx={{ fontSize: 40, color: isDragOver ? 'primary.main' : 'grey.500', mb: 1 }} />
                  <Typography variant="body1" fontWeight="medium">
                    Drag & drop packing list files here
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    or click to browse
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Supports: .xlsx, .xls, .xlsm, .xml, .pdf
                  </Typography>
                </Box>
              </label>
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                variant="contained"
                onClick={processAllFiles}
                disabled={fileStatuses.length === 0 || isProcessing}
                startIcon={isProcessing ? <CircularProgress size={20} /> : <ContainerIcon />}
                size="large"
                fullWidth
              >
                {isProcessing ? 'Processing...' : 'Process All Files'}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* File List with Progress */}
      {fileStatuses.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Files to Process
            </Typography>
            <List>
              {fileStatuses.map((fs, index) => (
                <ListItem
                  key={index}
                  sx={{
                    border: '1px solid',
                    borderColor: getStatusColor(fs.status),
                    borderRadius: 1,
                    mb: 1,
                    backgroundColor: fs.status === 'success' ? 'success.light' :
                                    fs.status === 'error' ? 'error.light' : 'transparent',
                    opacity: fs.status === 'success' || fs.status === 'error' ? 0.9 : 1,
                    cursor: fs.status === 'success' ? 'pointer' : 'default'
                  }}
                  onClick={() => fs.status === 'success' && setSelectedResultIndex(index)}
                  secondaryAction={
                    fs.status === 'pending' && !isProcessing ? (
                      <IconButton edge="end" onClick={() => removeFile(index)}>
                        <CloseIcon />
                      </IconButton>
                    ) : null
                  }
                >
                  <ListItemIcon>
                    {getStatusIcon(fs.status)}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <FileIcon fontSize="small" />
                        {fs.file.name}
                        {fs.result?.containers_created && (
                          <Chip
                            label={`${fs.result.containers_created} container${fs.result.containers_created > 1 ? 's' : ''}`}
                            size="small"
                            color="primary"
                          />
                        )}
                        {fs.result?.pallets_matched !== undefined && (
                          <Chip
                            label={`${fs.result.pallets_matched}/${fs.result.total_pallets_processed} matched`}
                            size="small"
                            color="success"
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      fs.status === 'error' ? (
                        <Typography variant="body2" color="error">
                          {fs.error}
                        </Typography>
                      ) : fs.status === 'uploading' ? (
                        <Box sx={{ mt: 1 }}>
                          <LinearProgress variant="determinate" value={fs.progress} />
                        </Box>
                      ) : fs.status === 'success' ? (
                        <Typography variant="body2" color="success.dark">
                          Click to view details
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {(fs.file.size / 1024).toFixed(1)} KB
                        </Typography>
                      )
                    }
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {/* Summary after processing */}
      {activeStep === 2 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              <SuccessIcon color="success" sx={{ mr: 1, verticalAlign: 'middle' }} />
              Import Complete
            </Typography>

            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item xs={6} md={3}>
                <Chip
                  label={`${successCount}/${fileStatuses.length} Files Successful`}
                  color={errorCount === 0 ? "success" : "warning"}
                  size="medium"
                />
              </Grid>
              {errorCount > 0 && (
                <Grid item xs={6} md={3}>
                  <Chip
                    label={`${errorCount} Failed`}
                    color="error"
                    size="medium"
                  />
                </Grid>
              )}
              <Grid item xs={6} md={3}>
                <Chip
                  label={`${totalContainersCreated} Containers Created`}
                  color="primary"
                  size="medium"
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              {totalContainersCreated > 0 && (
                <Button
                  variant="contained"
                  startIcon={<ContainerIcon />}
                  onClick={handleViewContainers}
                >
                  View All Containers
                </Button>
              )}
              <Button
                variant="outlined"
                startIcon={<ReportIcon />}
                href="/containers"
              >
                Go to Container Management
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Selected Result Details */}
      {selectedResult && selectedResult.success && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                Details: {fileStatuses[selectedResultIndex!]?.file.name}
              </Typography>
              <IconButton onClick={() => setSelectedResultIndex(null)}>
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Warning if items were skipped */}
            {selectedResult.warning && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {selectedResult.warning}
              </Alert>
            )}

            {/* PO Detection Breakdown */}
            {selectedResult.po_breakdown && selectedResult.po_breakdown.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                  Purchase Order Detection Summary:
                </Typography>
                <Alert severity={selectedResult.po_breakdown.length > 1 ? "info" : "success"} sx={{ mb: 2 }}>
                  {selectedResult.po_breakdown.length === 1 && selectedResult.po_breakdown[0].po_number ? (
                    <Typography variant="body2">
                      All lines matched to a single PO ({selectedResult.po_breakdown[0].po_number})
                    </Typography>
                  ) : (
                    <Typography variant="body2">
                      Multi-PO container detected: Lines matched to {selectedResult.po_breakdown.filter(po => po.po_number !== null).length} different POs
                    </Typography>
                  )}
                </Alert>

                {selectedResult.po_breakdown.map((po, idx) => (
                  <Box
                    key={po.po_number || 'unmatched'}
                    sx={{
                      p: 2,
                      mb: 2,
                      border: '1px solid',
                      borderColor: po.po_number ? (idx === 0 ? 'primary.main' : 'grey.300') : 'warning.main',
                      borderRadius: 1,
                      backgroundColor: po.po_number ? (idx === 0 ? 'primary.light' : 'grey.50') : 'warning.light',
                      opacity: po.po_number ? 1 : 0.8
                    }}
                  >
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="h6">
                        {po.po_number ? `PO ${po.po_number}` : 'Unmatched Lines'}
                        {idx === 0 && po.po_number && (selectedResult.po_breakdown?.length || 0) > 1 && (
                          <Chip label="Primary" size="small" color="primary" sx={{ ml: 1 }} />
                        )}
                      </Typography>
                      <Chip
                        label={`${po.line_count} line${po.line_count !== 1 ? 's' : ''}`}
                        color={po.po_number ? "primary" : "warning"}
                        size="small"
                      />
                    </Box>

                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {po.unique_stocks} unique stock code{po.unique_stocks !== 1 ? 's' : ''}:
                    </Typography>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                      {po.stock_codes.slice(0, 5).map((stockCode) => (
                        <Chip
                          key={stockCode}
                          label={stockCode}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                      {po.stock_codes.length > 5 && (
                        <Chip
                          label={`+${po.stock_codes.length - 5} more`}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>

                    {!po.po_number && (
                      <Alert severity="warning" sx={{ mt: 1 }}>
                        These stock codes were not found in any open orders. They may be completed/legacy items.
                      </Alert>
                    )}
                  </Box>
                ))}
              </Box>
            )}

            {/* Container Numbers */}
            {selectedResult.container_numbers && selectedResult.container_numbers.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Container Numbers Created:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {selectedResult.container_numbers.map((containerNo) => (
                    <Chip
                      key={containerNo}
                      label={containerNo.trim()}
                      variant="outlined"
                      size="small"
                    />
                  ))}
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Container Details Dialog */}
      <Dialog
        open={showContainers}
        onClose={() => setShowContainers(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Created Containers</DialogTitle>
        <DialogContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Container Number</TableCell>
                  <TableCell>PO Number</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>ID</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {containers.map((container) => (
                  <TableRow key={container.id}>
                    <TableCell>{container.container_no}</TableCell>
                    <TableCell>{container.po_number}</TableCell>
                    <TableCell>
                      <Chip label={container.status} size="small" color="default" />
                    </TableCell>
                    <TableCell>{container.id}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowContainers(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PackingListImport;
